import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Phone, Facebook, Twitter, Youtube, Instagram, Linkedin } from 'lucide-react';
import { useSiteData } from '../lib/SiteDataContext';
import { useLang } from '../lib/LanguageContext';
import { resolveImage } from '../lib/api';

const SocialIcon = ({ url, Icon, label }) => {
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-white/80 transition hover:border-[#C9A86A] hover:text-[#C9A86A]"
    >
      <Icon size={15} />
    </a>
  );
};

const Footer = () => {
  const { settings } = useSiteData();
  const { t } = useLang();
  const social = settings?.social || {};
  const contact = settings?.contact || {};
  const footer = settings?.footer || {};
  const govLinks = (settings?.government_links || [])
    .slice().sort((a, b) => a.order - b.order);
  const logo = settings?.logo_url ? resolveImage(settings.logo_url) : '';
  const footerAbout =
    settings?.footer_about ||
    'The official portal of the State House.';
  const siteName = settings?.site_name || 'South East State of Somalia';
  const copy = (footer.copyright_template || '© {year} {site_name}. All rights reserved.')
    .replace('{year}', String(new Date().getFullYear()))
    .replace('{site_name}', siteName);

  return (
    <footer className="bg-[#0B2545] text-white">
      <div className="mx-auto max-w-[1280px] px-6 py-16">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-3">
              {logo ? (
                <img src={logo} alt="Seal" className="h-12 w-auto" />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 font-serif text-lg font-bold text-[#C9A86A]">
                  {(siteName || 'S').charAt(0)}
                </div>
              )}
              <div className="font-serif text-[16px] font-semibold leading-tight">
                {siteName.split(' of ')[0] || siteName}
                <br />
                <span className="text-[#C9A86A]">
                  {siteName.includes(' of ') ? `of ${siteName.split(' of ')[1]}` : ''}
                </span>
              </div>
            </div>
            <p className="mt-5 text-[13px] leading-relaxed text-white/70">{footerAbout}</p>
            <div className="mt-5 flex items-center gap-3">
              <SocialIcon url={social.facebook} Icon={Facebook} label="Facebook" />
              <SocialIcon url={social.twitter} Icon={Twitter} label="Twitter" />
              <SocialIcon url={social.youtube} Icon={Youtube} label="YouTube" />
              <SocialIcon url={social.instagram} Icon={Instagram} label="Instagram" />
              <SocialIcon url={social.linkedin} Icon={Linkedin} label="LinkedIn" />
            </div>
          </div>

          <div>
            <h4 className="mb-5 font-serif text-[18px] font-semibold">{footer.government_heading || t('Government')}</h4>
            <ul className="space-y-3 text-[13.5px] text-white/75">
              {govLinks.map((g) => (
                <li key={g.slug}>
                  <Link to={`/government/${g.slug}`} className="hover:text-[#C9A86A]">{g.name}</Link>
                </li>
              ))}
              <li><Link to="/ministries" className="hover:text-[#C9A86A]">{t('All Ministries')}</Link></li>
              <li><Link to="/documents" className="hover:text-[#C9A86A]">{t('Documents')}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="mb-5 font-serif text-[18px] font-semibold">{footer.quick_links_heading || t('Quick Links')}</h4>
            <ul className="space-y-3 text-[13.5px] text-white/75">
              <li><Link to="/news" className="hover:text-[#C9A86A]">{t('News')}</Link></li>
              <li><Link to="/events" className="hover:text-[#C9A86A]">{t('Events')}</Link></li>
              <li><Link to="/investment/opportunities" className="hover:text-[#C9A86A]">{t('Investment')}</Link></li>
              <li><Link to="/about" className="hover:text-[#C9A86A]">{t('About the State')}</Link></li>
              <li><Link to="/contact" className="hover:text-[#C9A86A]">{t('Contact')}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="mb-5 font-serif text-[18px] font-semibold">{footer.contact_heading || t('Contact')}</h4>
            <ul className="space-y-4 text-[13.5px] text-white/75">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="mt-0.5 text-[#C9A86A]" />
                <span>{contact.address}</span>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={16} className="mt-0.5 text-[#C9A86A]" />
                <a href={`mailto:${contact.email}`} className="hover:text-[#C9A86A]">{contact.email}</a>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={16} className="mt-0.5 text-[#C9A86A]" />
                <span>{contact.phone}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-between gap-3 px-6 py-5 text-[12.5px] text-white/60 md:flex-row">
          <div>{copy}</div>
          <div className="flex items-center gap-5">
            {(footer.legal_links || []).map((l, i) => (
              <a key={i} href={l.href || '#'} className="hover:text-[#C9A86A]">{l.label}</a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
